import { useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client/react';

import {
    CurrentUserDocument,
    IRole,
    ITrainingRole,
    ITrainingSortBy,
    ITrainingsSelectorQueryVariables,
    TrainingsDocument,
} from 'graphql/types';
import { useApolloError } from 'common/hooks/useApolloError';
import { useSortBy } from 'common/hooks/useSortBy';
import { useTrainingFilter } from 'training/hooks/useTrainingFilter';
import { usePagination } from 'common/hooks/usePagination';

export const useTrainingList = (
    trainingQueryVariables?: ITrainingsSelectorQueryVariables,
    filterParamsInUrl = true,
    archived = false
) => {
    const { showApolloError } = useApolloError();

    const { data: currentUserData, loading: loadingCurrentUser } =
        useQuery(CurrentUserDocument);
    const isManager =
        currentUserData?.currentUser?.roles?.includes(IRole.Manager) ?? false;

    const {
        filters,
        filterValues,
        filterValuesForQuery,
        handleSearch: onSearch,
        handleFilterChange: onFilterChange,
        loading: loadingFilters,
    } = useTrainingFilter(filterParamsInUrl);

    const { paginationSettings, initializePagination, setPage } =
        usePagination(true);

    const { sortBy, onSort } = useSortBy<ITrainingSortBy>(
        ITrainingSortBy.Title
    );

    const paginationVariables = useMemo(
        () => ({
            offset: paginationSettings.offset,
            first: paginationSettings.first,
        }),
        [paginationSettings]
    );

    // Build variables for query
    const variables = useMemo(
        () => ({
            ...paginationVariables,
            ...filterValuesForQuery,
            ...trainingQueryVariables,
            archived,
            sortBy,
            // Non-managers can only manage trainings they are author of
            trainingUserRoles: isManager ? undefined : [ITrainingRole.Author],
        }),
        [
            paginationVariables,
            filterValuesForQuery,
            trainingQueryVariables,
            archived,
            sortBy,
            isManager,
        ]
    );

    const {
        loading: trainingsLoading,
        data,
        error,
    } = useQuery(TrainingsDocument, {
        variables,
        fetchPolicy: 'cache-and-network',
        // Wait for the current user before firing, otherwise the query runs
        // once with a provisional (non-manager) `trainingUserRoles` and then
        // re-fires once `isManager` resolves.
        skip: loadingCurrentUser,
    });

    useEffect(() => {
        if (error) {
            showApolloError(error);
        }
    }, [error, showApolloError]);

    const trainingsObject = data?.paginatedTrainings;
    const trainingsCount = trainingsObject?.count;
    const trainings = useMemo(
        () =>
            trainingsObject?.edges
                .map((edge) => edge?.node || null)
                .filter(Boolean) || [],
        [trainingsObject?.edges]
    );

    const loading = trainingsLoading || loadingFilters || loadingCurrentUser;

    useEffect(() => {
        if (loading || !initializePagination) return;

        initializePagination(trainingsCount || 0);
    }, [trainingsCount, loading, initializePagination]);

    return {
        trainings,
        trainingsCount,
        loading,
        paginationSettings,
        filters,
        filterValues,
        onSearch,
        onFilterChange,
        setPage,
        onSort,
        currentSort: sortBy,
    };
};
